import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import {
  Search,
  Users,
  X,
  Check,
  Mail,
  UserPlus,
  Building2,
} from "lucide-react";
import { getApiUrl } from "../../config/api";
import SendInviteDialog from "../Invites/SendInviteDialog";

const AddUserToOrganization = ({
  organizationId,
  businessUnits,
  onUserAdded,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state for role assignment
  const [selectedRole, setSelectedRole] = useState("business_user");
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState("");

  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        getApiUrl(`users/search?q=${encodeURIComponent(searchTerm)}`),
        {
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to search users");
      }

      const data = await response.json();
      setSearchResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const assignUserToOrganization = async (userId) => {
    try {
      setAssignLoading(true);
      setError(null);

      let response;
      let successMessage;

      switch (selectedRole) {
        case "org_admin":
          response = await fetch(
            getApiUrl(`organizations/${organizationId}/assign-admin`),
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${await getAccessTokenSilently()}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ user_id: userId }),
            }
          );
          successMessage = "Organization admin assigned successfully!";
          break;

        case "business_unit_head":
          if (!selectedBusinessUnit) {
            throw new Error(
              "Business unit selection is required for business unit heads"
            );
          }
          response = await fetch(
            getApiUrl(`business-units/${selectedBusinessUnit}/assign-head`),
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${await getAccessTokenSilently()}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ user_id: userId }),
            }
          );
          successMessage = "Business unit head assigned successfully!";
          break;

        case "business_user":
          if (!selectedBusinessUnit) {
            throw new Error(
              "Business unit selection is required for business users"
            );
          }
          response = await fetch(
            getApiUrl(
              `business-units/${selectedBusinessUnit}/assign-business-user`
            ),
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${await getAccessTokenSilently()}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ user_id: userId }),
            }
          );
          successMessage = "Business user assigned successfully!";
          break;

        default:
          throw new Error("Invalid role selection");
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to assign user");
      }

      setSuccess(successMessage);
      setSearchTerm("");
      setSearchResults([]);
      setSelectedRole("business_user");
      setSelectedBusinessUnit("");

      if (onUserAdded) {
        onUserAdded();
      }

      // Close dialog after successful assignment
      setTimeout(() => {
        setIsOpen(false);
        setSuccess(null);
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setAssignLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const resetDialog = () => {
    setSearchTerm("");
    setSearchResults([]);
    setSelectedRole("business_user");
    setSelectedBusinessUnit("");
    setError(null);
    setSuccess(null);
  };

  const getRoleLabel = (role) => {
    const roleMap = {
      org_admin: "Organization Admin",
      business_unit_head: "Business Unit Head",
      business_user: "Business User",
    };
    return roleMap[role] || role;
  };

  const requiresBusinessUnit =
    selectedRole === "business_unit_head" || selectedRole === "business_user";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) resetDialog();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" className="flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add User to Organization</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Search for existing users or invite new ones to join your
            organization
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {error && <Alert variant="destructive">{error}</Alert>}

          {success && (
            <Alert className="border-green-200 bg-green-50 text-green-800">
              <Check className="h-4 w-4" />
              {success}
            </Alert>
          )}

          {/* Role Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="org_admin">Organization Admin</SelectItem>
                  <SelectItem value="business_unit_head">
                    Business Unit Head
                  </SelectItem>
                  <SelectItem value="business_user">Business User</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Business Unit Selection (conditional) */}
            {requiresBusinessUnit && (
              <div className="space-y-2">
                <Label htmlFor="businessUnit">Business Unit *</Label>
                <Select
                  value={selectedBusinessUnit}
                  onValueChange={setSelectedBusinessUnit}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a business unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessUnits.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Search Results */}
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-2">
                {searchResults.map((user) => (
                  <Card key={user.id} className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback>
                            {user.name
                              ?.split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">{user.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => assignUserToOrganization(user.id)}
                        disabled={
                          assignLoading ||
                          (requiresBusinessUnit && !selectedBusinessUnit)
                        }
                        className="flex items-center gap-1"
                      >
                        {assignLoading ? (
                          <Spinner size="sm" />
                        ) : (
                          <Users className="h-3 w-3" />
                        )}
                        Add as {getRoleLabel(selectedRole)}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            ) : searchTerm.trim() && !loading ? (
              <div className="text-center py-4 text-muted-foreground">
                <div className="space-y-3">
                  <p>No users found matching "{searchTerm}"</p>
                  <div className="text-xs text-muted-foreground">
                    Can't find the user? You can send them an invitation
                    instead.
                  </div>
                  <div className="flex justify-center">
                    <SendInviteDialog
                      organizationId={organizationId}
                      businessUnitId={
                        requiresBusinessUnit ? selectedBusinessUnit : null
                      }
                      businessUnit={
                        requiresBusinessUnit
                          ? businessUnits.find(
                              (bu) => bu.id === selectedBusinessUnit
                            )
                          : null
                      }
                      defaultRole={selectedRole}
                      onInviteSent={onUserAdded}
                    >
                      <Button
                        variant={
                          requiresBusinessUnit && !selectedBusinessUnit
                            ? "outline"
                            : "default"
                        }
                        size="sm"
                        className="flex items-center gap-2"
                        disabled={requiresBusinessUnit && !selectedBusinessUnit}
                      >
                        <Mail className="h-4 w-4" />
                        Send Invitation
                      </Button>
                    </SendInviteDialog>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {searchTerm.trim() === "" && (
            <div className="text-center py-4 text-muted-foreground text-sm space-y-3">
              <div className="flex items-center justify-center gap-2">
                <Search className="h-4 w-4" />
                <span>Start typing to search for existing users</span>
              </div>
              <div className="text-xs">Or send an invitation to a new user</div>
              <div className="flex justify-center">
                <SendInviteDialog
                  organizationId={organizationId}
                  businessUnitId={
                    requiresBusinessUnit ? selectedBusinessUnit : null
                  }
                  businessUnit={
                    requiresBusinessUnit
                      ? businessUnits.find(
                          (bu) => bu.id === selectedBusinessUnit
                        )
                      : null
                  }
                  defaultRole={selectedRole}
                  onInviteSent={onUserAdded}
                >
                  <Button
                    variant={
                      requiresBusinessUnit && !selectedBusinessUnit
                        ? "outline"
                        : "default"
                    }
                    size="sm"
                    className="flex items-center gap-2"
                    disabled={requiresBusinessUnit && !selectedBusinessUnit}
                  >
                    <UserPlus className="h-4 w-4" />
                    Invite New User
                  </Button>
                </SendInviteDialog>
              </div>
            </div>
          )}

          {/* Role Requirements Notice */}
          {requiresBusinessUnit && !selectedBusinessUnit && (
            <Alert>
              <Building2 className="h-4 w-4" />
              <div>
                <strong>Business Unit Required</strong>
                <p className="text-sm mt-1">
                  Please select a business unit to assign{" "}
                  {getRoleLabel(selectedRole).toLowerCase()} roles.
                </p>
              </div>
            </Alert>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserToOrganization;
